import { useRef, useState, useCallback, useEffect } from "react";
import { TranscriptEntry } from "./types";

const SAMPLE_RATE = 16000;
interface UseNovaSocketOptions {
  wsUrl: string;
  sessionId: string;
  orgId: string;
  userId: string;
  persona: string;
  scenarioName: string;
  scenarioDesc: string;
  voiceId: string;
  onTranscriptUpdate?: (entries: TranscriptEntry[]) => void;
  onError?: (error: Error) => void;
}

interface UseNovaSocketReturn {
  isConnected: boolean;
  isAiSpeaking: boolean;
  transcript: TranscriptEntry[];
  connect: () => Promise<void>;
  disconnect: () => void;
  sendAudioChunk: (base64Audio: string) => void;
}

export function useNovaSocket(options: UseNovaSocketOptions): UseNovaSocketReturn {
  const { wsUrl, sessionId, orgId, userId, persona, scenarioName, scenarioDesc, voiceId, onTranscriptUpdate, onError } = options;
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const audioQueueRef = useRef<Float32Array[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const isPlayingRef = useRef(false);

  // Queue-based audio playback — clear on interruption
  const playNextChunk = useCallback(async () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0) return;
    isPlayingRef.current = true;

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
    }
    const ctx = audioContextRef.current;

    while (audioQueueRef.current.length > 0) {
      const chunk = audioQueueRef.current.shift()!;
      const buffer = ctx.createBuffer(1, chunk.length, SAMPLE_RATE);
      buffer.getChannelData(0).set(chunk);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      await new Promise<void>((resolve) => {
        source.onended = () => resolve();
        source.start();
      });
    }

    isPlayingRef.current = false;
    setIsAiSpeaking(false);
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);

      switch (data.type) {
        // Strands BidiAgent events
        case "bidi_audio_stream": {
          setIsAiSpeaking(true);
          const base64 = data.audio;
          if (base64) {
            const binaryString = atob(base64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            // Decode Int16 PCM to Float32
            const int16 = new Int16Array(bytes.buffer);
            const float32 = new Float32Array(int16.length);
            for (let i = 0; i < int16.length; i++) {
              float32[i] = int16[i] / 32768;
            }
            audioQueueRef.current.push(float32);
            playNextChunk();
          }
          break;
        }

        case "bidi_transcript_stream": {
          const text = data.current_transcript || data.text || "";
          const role = data.role === "user" ? "user" : "assistant";
          if (text.trim() && data.is_final) {
            const entry: TranscriptEntry = { role, message: text.trim(), timestamp: new Date().toISOString() };
            setTranscript((prev) => {
              const updated = [...prev, entry];
              onTranscriptUpdate?.(updated);
              return updated;
            });
            if (role === "assistant") setIsAiSpeaking(false);
          }
          break;
        }

        case "bidi_interruption": {
          // Barge-in: clear audio queue
          audioQueueRef.current = [];
          setIsAiSpeaking(false);
          break;
        }

        case "error": {
          onError?.(new Error(data.message || "Voice stream error"));
          break;
        }
      }
    } catch (err) {
      console.error("Failed to parse WebSocket message:", err);
    }
  }, [onTranscriptUpdate, onError, playNextChunk]);

  const connect = useCallback(async () => {
    if (wsRef.current) return;

    const params = new URLSearchParams({
      sessionId, orgId, userId, persona, scenarioName, scenarioDesc, voiceId,
    });

    return new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(`${wsUrl}?${params}`);

      ws.onopen = () => {
        wsRef.current = ws;
        setIsConnected(true);
        resolve();
      };

      ws.onmessage = handleMessage;

      ws.onerror = () => {
        onError?.(new Error("WebSocket connection failed"));
        reject(new Error("WebSocket connection failed"));
      };

      ws.onclose = () => {
        wsRef.current = null;
        setIsConnected(false);
        setIsAiSpeaking(false);
      };
    });
  }, [wsUrl, sessionId, orgId, userId, persona, scenarioName, scenarioDesc, voiceId, handleMessage, onError]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setIsAiSpeaking(false);
    audioQueueRef.current = [];
  }, []);

  // Send audio in Strands bidi_audio_input format
  const sendAudioChunk = useCallback((base64Audio: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      type: "bidi_audio_input",
      audio: base64Audio,
      format: "pcm",
      sample_rate: 16000,
      channels: 1,
    }));
  }, []);

  useEffect(() => {
    return () => { disconnect(); };
  }, [disconnect]);

  return { isConnected, isAiSpeaking, transcript, connect, disconnect, sendAudioChunk };
}
